import React from 'react';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import styled, { css, keyframes } from 'styled-components';
import { FieldLabel } from '../form/field-label/FieldLabel';
import { Input } from '../form/input/Input';
import { Icon } from '../icon/Icon';
import { useDropdownMenu } from '../dropdown/useDropdownMenu';
import { menuMotionMs } from '../dropdown/Menu.styled';
import { HistoryCalendarValue, HistoryCalendarView } from './HistoryCalendarView';

const ICON_GUTTER = 32;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

// The icon sits over the box rather than beside it, so the input keeps its own
// width and stroke — only the text is kept clear of the glyph.
const TriggerBox = styled.div`
	position: relative;
	display: flex;
`;

// Matches the dropdown menu's entrance, so a calendar and a menu opening off
// the same form read as the same gesture.
const reveal = keyframes`
	from {
		opacity: 0;
		translate: 0 -4px;
	}
`;

const dismiss = keyframes`
	to {
		opacity: 0;
		translate: 0 -4px;
	}
`;

// Floats over the form rather than pushing it down: a date is a choice made and
// dismissed, and growing the page under the pointer moves whatever the user was
// reading. Portalled, so an ancestor's `overflow` cannot clip it.
const Popover = styled.div<{ $closing?: boolean }>`
	width: fit-content;
	border-radius: 4px;
	box-shadow: ${props => props.theme.form.dropdown.menu.shadow};
	z-index: 45;
	animation: ${props => props.$closing ? dismiss : reveal}
		${menuMotionMs}ms
		${props => props.theme.motion.easing.default} forwards;

	// A closing calendar is still on screen; it must not take the pointer.
	${({ $closing }) => $closing && css`
		pointer-events: none;
	`}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

// The box is a display of the choice, not somewhere to type — the calendar is
// the only way in, so the trigger is read-only and the pointer says "press me".
const Trigger = styled(Input)<{ $hasIcon?: boolean }>`
	cursor: pointer;
	${({ $hasIcon }) => $hasIcon && `padding-right: ${ICON_GUTTER}px;`}
`;

// Transparent to the pointer, so clicking the glyph opens the calendar exactly
// as clicking the box does.
const TriggerIcon = styled(Icon)`
	position: absolute;
	right: 10px;
	top: 50%;
	translate: 0 -50%;
	pointer-events: none;
	color: ${props => props.theme.text.tertiary};
	font-size: 16px;
`;

const pad = (value: number) => String(value).padStart(2, '0');

/** '09 Sep 2026', or '09 Sep 2026 11:30' when the time is part of the choice. */
export const formatDate = (date: Date | null, includeTime = false): string => {
	if (!date) return '';
	const stamp = `${pad(date.getDate())} ${date.toLocaleDateString('en', { month: 'short' })} ${date.getFullYear()}`;
	return includeTime ? `${stamp} ${pad(date.getHours())}:${pad(date.getMinutes())}` : stamp;
};

/**
 * `<input type="datetime-local">`'s 'YYYY-MM-DDTHH:MM' — a wall-clock time with no offset, which is
 * what the registration forms send and receive. An offset-less date-time parses as browser-local and
 * is formatted back with the local getters, so the wall clock survives whatever zone the browser is in.
 */
export const parseLocalDateTime = (value: string): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	return isNaN(date.getTime()) ? null : date;
};

export const formatLocalDateTime = (date: Date | null): string => {
	if (!date) return '';
	const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
	return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export type DatePickerFieldProps = {
	value: Date | null;
	onChange: (next: Date | null) => void;
	/** Omit for a bare field — the caller owns the wording, including translation. */
	label?: React.ReactNode;
	placeholder?: string;
	/** Adds hours and minutes below the grid, and to the trigger's text. */
	includeTime?: boolean;
	/** Shows a calendar glyph inside the right-hand end of the box. */
	showIcon?: boolean;
	disabled?: boolean;
	hasError?: boolean;
	/** Lands on the trigger, so an outside `<label htmlFor>` points at it. */
	id?: string;
	className?: string;
};

export function DatePickerField({
	value,
	onChange,
	label,
	placeholder,
	includeTime = false,
	showIcon = false,
	disabled = false,
	hasError = false,
	id,
	className,
}: DatePickerFieldProps) {
	// The same machinery every dropdown in the library opens on: popper for the
	// placement, a portal so nothing clips it, outside-click, and a mount that
	// outlives the close so the exit can play.
	const { onToggle, onClose, triggerRef, menuProps, renderMenu } =
		useDropdownMenu<HTMLDivElement>({ placement: 'bottom-start' });

	const handleChange = (next: HistoryCalendarValue) => {
		if (next?.mode !== 'single') {
			onChange(null);
			return;
		}
		onChange(next.date);
		// A date-only choice is finished the moment a day is clicked; with the
		// time panel the calendar has to stay open to set the hours.
		if (!includeTime) onClose();
	};

	return (
		<Field className={className}>
			{label && <FieldLabel>{label}</FieldLabel>}
			<TriggerBox ref={triggerRef}>
				<Trigger
					id={id}
					readOnly
					hasError={hasError}
					disabled={disabled}
					$hasIcon={showIcon}
					value={formatDate(value, includeTime)}
					placeholder={placeholder}
					onClick={() => !disabled && onToggle()}
				/>
				{showIcon && <TriggerIcon icon="calendar" />}
			</TriggerBox>
			{!disabled && renderMenu(
				<Popover
					ref={menuProps.ref}
					style={menuProps.popperStyles}
					$closing={menuProps.closing}
					{...menuProps.popperAttributes}
				>
					<HistoryCalendarView
						open
						mode="single"
						value={value ? { mode: 'single', date: value } : null}
						onChange={handleChange}
						pickerProps={includeTime
							? { plugins: [<TimePicker key="time" position="bottom" hideSeconds />] }
							: undefined}
					/>
				</Popover>
			)}
		</Field>
	);
}
